<template>
  <UCard :ui="pricesCard" :class="getCardClasses">
    <div v-if="isMobile" class="grid grid-cols-8 gap-2">
      <!-- IMAGEN -->
      <div
        role="button"
        tabindex="0"
        class="col-span-2"
        @click.stop="openModal()"
        @keydown.enter="openModal()"
      >
        <TLazyimg
          v-if="rentUnit?.media.length"
          :src="useCottageMedia().getPhotoItems(rentUnit.media)[0].url"
          :alt="useCottageMedia().getPhotoItems(rentUnit.media)[0].alt"
          classes="block w-full h-none aspect-w-4 aspect-h-3"
          classes-img="object-cover absolute inset-0 h-full w-full rounded-lg cursor-pointer"
        />
      </div>
      <!-- NOMBRE UA -->
      <div class="col-span-6">
        <UButton
          variant="link"
          color="primary"
          class="text-left mb-1"
          @click.stop="openModal()"
        >
          {{ t(`views.cottage-prices.cottage-types.${rentUnit?.type}`) }}
        </UButton>
        <TText classes="mb-0" size="sm">{{ rentUnit?.name }}</TText>
      </div>
    </div>

    <div class="md:grid md:grid-cols-8 md:gap-x-3">
      <div
        v-if="!isMobile"
        role="button"
        tabindex="0"
        class="md:col-span-2 md:col-start-1"
        @click.stop="openModal()"
        @keydown.enter="openModal()"
      >
        <TLazyimg
          v-if="rentUnit?.media.length"
          :src="useCottageMedia().getPhotoItems(rentUnit.media)[0].url"
          :alt="useCottageMedia().getPhotoItems(rentUnit.media)[0].alt"
          classes="block w-full h-full min-h-32"
          classes-img="object-cover absolute inset-0 h-full w-full rounded-lg cursor-pointer"
        />
      </div>

      <div class="md:col-span-6 md:col-start-3 md:flex md:flex-col">
        <div v-if="!isMobile" class="">
          <UButton
            variant="link"
            color="primary"
            :padded="false"
            class="text-left mb-1 leading-5"
            @click.stop="openModal()"
          >
            {{ t(`views.cottage-prices.cottage-types.${rentUnit?.type}`) }}
          </UButton>
          <TText classes="mb-0" size="sm">{{ rentUnit?.name }}</TText>
        </div>

        <!-- CARACTERISTICAS -->
        <ul class="w-full flex flex-wrap gap-2 mt-2">
          <li class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-material-symbols-light:airline-seat-individual-suite-rounded'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.capacity', rentUnit?.capacity)
            }}</TText>
          </li>
          <li v-if="rentUnit?.marriageBeds" class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-material-symbols-light:bed-rounded'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.marriageBeds', rentUnit?.marriageBeds)
            }}</TText>
          </li>
          <li v-if="rentUnit?.singleBeds" class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-material-symbols-light:single-bed-rounded'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.singleBeds', rentUnit?.singleBeds)
            }}</TText>
          </li>
          <li v-if="rentUnit?.additionalBeds" class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-material-symbols-light:airline-seat-individual-suite-rounded'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.additionalBeds', rentUnit?.additionalBeds)
            }}</TText>
          </li>
          <li v-if="rentUnit?.sofaBeds" class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-fluent:couch-20-filled'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.sofaBeds', rentUnit?.sofaBeds)
            }}</TText>
          </li>
          <li v-if="rentUnit?.bathrooms" class="flex items-center">
            <UIcon
              class="h-5 w-5 text-escapadaGray-500"
              aria-hidden="true"
              :name="'i-material-symbols-light:bathtub-rounded'"
            />
            <TText size="xs" classes="mb-0 ml-0.5">{{
              t('views.cottage-prices.bathrooms', rentUnit?.bathrooms)
            }}</TText>
          </li>
        </ul>

        <!-- BADGES -->
        <div
          v-if="showBadgeContainer"
          data-test="onlineBookingBadge"
          class="flex flex-wrap gap-1 md:gap-2 mt-2 md:min-h-6"
        >
          <UBadge
            v-if="showOnlineBooking"
            class=""
            color="info"
            variant="subtle"
            size="md"
            >{{ t('views.cottage-prices.onlineBooking') }}</UBadge
          >
          <UBadge
            v-if="pricesStore.isFreeCancellation"
            class=""
            color="success"
            variant="subtle"
            size="md"
            >{{ t('views.cottage-prices.freeCancellation') }}</UBadge
          >
          <div v-if="pricesStore.isFreeCancellation" class="flex items-center">
            <UIcon
              name="i-heroicons-check-solid"
              class="w-5 text-green-600 mr-1"
            />
            <TText size="sm" classes="mb-0">{{
              t('views.cottage-prices.freeCancellationText')
            }}</TText>
          </div>
        </div>
        <div class="md:flex-1 md:flex md:gap-x-3 md:w-full md:justify-between">
          <!-- SELECT -->
          <div
            class="md:flex-1 md:flex md:flex-col md:items-start md:justify-end md:text-end md:max-w-72"
          >
            <template
              v-if="
                inState([
                  states.READY_WITH_PRICES,
                  states.READY_WITH_PRICES_DISABLED,
                ]) && !hideSelect
              "
            >
              <TText variant="semibold" classes="mb-1 mt-1" size="sm">
                {{ t('views.cottage-prices.selectNumPeople') }}
              </TText>
              <USelect
                v-model="selectedCapacity"
                :items="prices"
                label-key="places"
                value-key="places"
                :disabled="!inState(['READY_WITH_PRICES'])"
                class="w-full"
                size="sm"
                @click.stop
              />
            </template>
          </div>
          <!-- BUTTONS & PRICE -->
          <div
            class="md:shrink-0 md:flex md:items-end md:justify-end md:text-end mt-4 md:mt-1"
          >
            <UButton
              v-if="
                inState([
                  states.DISABLED,
                  states.READY_NO_PRICES,
                  states.READY_MIN_NIGHTS_REQUIRED,
                  states.READY_NO_VACANCY,
                ])
              "
              :ui="mobileFullWidthButton"
              color="neutral"
              variant="subtle"
              :label="t('views.cottage-prices.seePrices')"
              size="xs"
              class="mt-4 md:mt-0"
              :disabled="
                inState([
                  states.DISABLED,
                  states.READY_MIN_NIGHTS_REQUIRED,
                  states.READY_NO_VACANCY,
                ])
              "
              @click="submitPricesForm"
            />
            <div
              v-if="
                inState([
                  states.READY_WITH_PRICES,
                  states.READY_WITH_PRICES_DISABLED,
                ])
              "
            >
              <div class="flex items-center md:justify-end gap-1 w-full">
                <UBadge
                  v-if="hasActiveDiscount"
                  color="error"
                  variant="subtle"
                  size="md"
                  icon="i-lucide-tag"
                  class="mr-2"
                >
                  {{ t('views.cottage-prices.with-prices.deal') }}
                </UBadge>

                <TText
                  v-if="getOldPrice()"
                  size="lg"
                  color="text-gray-900"
                  classes="mb-0 line-through"
                >
                  {{ toEuro(getOldPrice()!) }}
                </TText>
                <TText
                  size="lg"
                  variant="semibold"
                  color="text-gray-900"
                  classes="mb-0"
                >
                  {{ toEuro(getPrice()) }}
                </TText>
              </div>

              <TText size="sm" classes="mt-1 mb-0 text-nowrap">
                <i18n-t
                  keypath="views.cottage-prices.with-prices.description"
                  tag="span"
                  scope="global"
                >
                  <template #people>
                    <strong>{{
                      t(
                        'views.cottage-prices.with-prices.people',
                        Number(selectedCapacity),
                      )
                    }}</strong>
                  </template>
                  <template #nights>
                    <span class="font-bold">{{
                      t(
                        'views.cottage-prices.with-prices.nights',
                        calculateNights(
                          route.query.dateIn as string,
                          route.query.dateOut as string,
                        ),
                      )
                    }}</span>
                  </template>
                </i18n-t>
              </TText>
              <UButton
                v-if="pricesStore.isRentUnitSelected(props.rentUnitId)"
                :ui="mobileFullWidthButton"
                color="neutral"
                variant="subtle"
                :label="t('views.cottage-prices.with-prices.remove-rent-unit')"
                size="xs"
                class="mt-4 md:mt-2"
                @click="toggleSelect()"
              />
              <UButton
                v-if="!pricesStore.isRentUnitSelected(props.rentUnitId)"
                :ui="mobileFullWidthButton"
                color="primary"
                variant="solid"
                :label="t('views.cottage-prices.with-prices.add-rent-unit')"
                :disabled="inState([states.READY_WITH_PRICES_DISABLED])"
                size="xs"
                class="mt-4 md:mt-2"
                @click="toggleSelect()"
              />
            </div>
          </div>
        </div>

        <!-- NO VACANCY -->
        <div
          v-if="inState([states.READY_NO_VACANCY])"
          class="flex mt-4 md:mt-2 text-red-700 font-bold items-center md:justify-end"
        >
          <UIcon name="heroicons:information-circle-solid" />
          <TText
            size="sm"
            variant="semibold"
            color="text-red-700"
            classes="mb-0 ml-1"
          >
            <i18n-t
              keypath="views.cottage-prices.no-vacancy.description"
              tag="span"
              scope="global"
            >
              <template #action>
                <UButton
                  variant="link"
                  color="error"
                  @click.stop="openDateModal"
                >
                  {{ t('views.cottage-prices.no-vacancy.action') }}
                </UButton>
              </template>
            </i18n-t>
          </TText>
        </div>
        <!-- MIN NIGHTS REQUIRED -->
        <div
          v-if="inState([states.READY_MIN_NIGHTS_REQUIRED])"
          class="flex mt-4 md:mt-2 text-red-700 font-bold items-center md:justify-end"
        >
          <UIcon name="heroicons:information-circle-solid" />
          <TText
            size="sm"
            variant="semibold"
            color="text-red-700"
            classes="mb-0 ml-1"
          >
            <i18n-t
              keypath="views.cottage-prices.min-nights-required.description"
              tag="span"
              scope="global"
            >
              <template #nights>
                {{
                  t(
                    'views.cottage-prices.min-nights-required.nights',
                    minNights ?? 0,
                  )
                }}
              </template>
              <template #action>
                <UButton
                  variant="link"
                  color="error"
                  @click.stop="openDateModal"
                >
                  {{ t('views.cottage-prices.min-nights-required.action') }}
                </UButton>
              </template>
            </i18n-t>
          </TText>
        </div>
        <!-- DEPENDENT DISABLED RENT UNIT -->
        <div
          v-if="inState([states.READY_WITH_PRICES_DISABLED])"
          class="flex mt-4 md:mt-2 font-bold items-center md:justify-end"
        >
          <UIcon name="heroicons:information-circle-solid" />
          <TText size="sm" variant="semibold" classes="mb-0 ml-1">
            {{ t('views.cottage-prices.dependent-message') }}
          </TText>
        </div>
      </div>
    </div>
  </UCard>
</template>
<script setup lang="ts">
import { useCottageMedia } from '~~/modules/views/cottage-prices/composables/useCottageMedia'
import { pricesCard } from '~~/modules/design-system/utils/nuxt-ui-config/components/card/index'
import { mobileFullWidthButton } from '~~/modules/design-system/utils/nuxt-ui-config/components/button'
import { usePricesStore } from '~~/modules/views/cottage-prices/stores/pricesStore'

const { isMobile } = useDevice()

const states = {
  DISABLED: 'DISABLED',
  READY_NO_PRICES: 'READY_NO_PRICES',
  READY_NO_VACANCY: 'READY_NO_VACANCY',
  READY_MIN_NIGHTS_REQUIRED: 'READY_MIN_NIGHTS_REQUIRED',
  READY_WITH_PRICES: 'READY_WITH_PRICES',
  READY_WITH_PRICES_DISABLED: 'READY_WITH_PRICES_DISABLED',
  SELECTED: 'SELECTED',
}

const route = useRoute()
const pricesStore = usePricesStore()

const props = defineProps<{
  rentUnitId: string
  maximumCapacity: number
  currentState: string
  minNights: number
  hideSelect?: boolean
  showOnlineBooking?: boolean
}>()

// Get data from store using rentUnitId
const rentUnit = pricesStore.getRentUnitById(props.rentUnitId)
const rentUnitPrices = computed(() =>
  pricesStore.getRentUnitPricesById(props.rentUnitId),
)
const prices = computed(() => rentUnitPrices.value?.pricesByPlaces || [])

const initialCapacity: Number =
  prices.value.find(price => {
    return price.places === Number(route.query.capacity)
  })?.places || 1
const localSelectedCapacity = ref(Number(initialCapacity))
const selectedCapacity = computed({
  get: () => {
    return (
      pricesStore.getRentUnitSelectedPeople(props.rentUnitId) ||
      localSelectedCapacity.value
    )
  },
  set: (value: number) => {
    localSelectedCapacity.value = value
    pricesStore.updateRentUnitPeople(props.rentUnitId, value)
  },
})

const emit = defineEmits([
  'toggle-select',
  'open-modal',
  'open-date-modal',
  'submit-prices-form',
])

const inState = (options: string[]) => {
  return options.includes(props.currentState)
}

const toggleSelect = () => {
  if (!rentUnit) return

  // Cuando se selecciona, usar el valor local actual
  if (!pricesStore.isRentUnitSelected(props.rentUnitId)) {
    // Emitir el evento con el valor local actual
    emit('toggle-select', {
      ...rentUnit,
      selectedPeople: localSelectedCapacity.value,
    })
  } else {
    // Cuando se deselecciona, emitir normalmente
    emit('toggle-select', rentUnit)
  }
}

const openModal = () => {
  if (rentUnit) {
    emit('open-modal', rentUnit)
  }
}

const openDateModal = () => {
  emit('open-date-modal')
}

const submitPricesForm = () => {
  emit('submit-prices-form')
}

// selectCapacity is no longer needed - the computed setter handles updates automatically
const getPrice = () => {
  return (
    prices.value.find(price => {
      return price.places === Number(selectedCapacity.value)
    })?.price.amount || 0
  )
}

const getOldPrice = () => {
  return (
    prices.value.find(price => {
      return price.places === Number(selectedCapacity.value)
    })?.oldPrice?.amount || null
  )
}

const hasActiveDiscount = computed(() => {
  return (
    prices.value.find(price => {
      return price.places === Number(selectedCapacity.value)
    })?.isDeal || false
  )
})

const showBadgeContainer = computed(() => {
  return props.showOnlineBooking || pricesStore.isFreeCancellation
})

const getCardClasses = computed(() => {
  const baseClasses = 'transition-all ease-in-out duration-300 '

  if (
    inState([
      states.READY_MIN_NIGHTS_REQUIRED,
      states.READY_NO_VACANCY,
      states.DISABLED,
      states.READY_WITH_PRICES_DISABLED,
    ])
  ) {
    return baseClasses + 'shadow-none bg-gray-100'
  } else if (
    inState([states.READY_WITH_PRICES]) &&
    pricesStore.isRentUnitSelected(props.rentUnitId)
  ) {
    return (
      baseClasses +
      'bg-primary-50 border border-white shadow-lg outline outline-offset-0 outline-3 outline-primary-400'
    )
  } else if (
    inState([states.READY_WITH_PRICES]) &&
    !pricesStore.isRentUnitSelected(props.rentUnitId)
  ) {
    return (
      baseClasses +
      'shadow border outline outline-offset-0 outline-3 outline-transparent'
    )
  } else {
    return baseClasses + 'shadow'
  }
})
</script>
